import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Compass, Settings, Gamepad2, Home as HomeIcon, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import { getMemories, addMemory } from "@/lib/memories";
import { useKeyboardMovement } from "@/hooks/useKeyboardMovement";
import { STORE_ZONES, STREET_ZONES, SHOPKEEPER_LINES } from "@/data/zones";
import { Avatar } from "@/components/game/Avatar";
import { Zone } from "@/components/game/Zone";
import { StoreDecor } from "@/components/game/StoreDecor";
import { StreetDecor } from "@/components/game/StreetDecor";
import { StreetNPCs } from "@/components/game/StreetNPCs";
import { RotaryPhone } from "@/components/game/RotaryPhone";
import { startGali, setGaliTime, stopGali } from "@/lib/galiAmbience";
import { sfx } from "@/lib/soundfx";
import { ShopkeeperDialogue } from "@/components/game/ShopkeeperDialogue";
import { NostalgiaCard } from "@/components/NostalgiaCard";
import { ZoneShelf } from "@/components/ZoneShelf";
import { CollectionPanel } from "@/components/CollectionPanel";
import { AmbientMusic } from "@/components/AmbientMusic";
import { MoveHint } from "@/components/game/MoveHint";
import { useIsTouch } from "@/hooks/useIsTouch";
import { WeighingGame } from "@/components/kirana/WeighingGame";
import { GolaMaker } from "@/components/kirana/GolaMaker";
import { LuckyPack } from "@/components/kirana/LuckyPack";

const ACTIVITIES = [
  { id: "weigh", emoji: "⚖️", label: "Taraazu", x: 36, y: 63 },
  { id: "gola", emoji: "🧊", label: "Gola Wala", x: 52, y: 84 },
  { id: "pack", emoji: "🎁", label: "Lucky Packet", x: 90, y: 58 },
  { id: "parchi", emoji: "📝", label: "Mummy ki Parchi", x: 49, y: 14 },
  { id: "switch", emoji: "💡", label: "Light Switch", x: 90, y: 16 },
];
const QUEST_LABELS = { "candy-jars": "Toffee/Candy", "chocolate-counter": "Chocolate", "biscuit-shelf": "Biscuit", "chips-wall": "Chips/Namkeen", "drinks-fridge": "Cold Drinks", "churan-counter": "Churan" };
const QUEST_POOL = Object.keys(QUEST_LABELS);
const TOD_ORDER = ["morning", "afternoon", "evening", "night"];
const TOD_LABEL = { morning: "🌅 Subah", afternoon: "☀️ Dopahar", evening: "🌇 Shaam", night: "🌙 Raat" };

const BOUNDS = { minX: 4, maxX: 96, minY: 14, maxY: 92 };
const THRESHOLD = 9;
const STREET_START = { x: 50, y: 74 };
const STORE_START = { x: 12, y: 82 };

export default function KiranaStore() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scene, setScene] = useState("street");
  const [memories, setMemories] = useState(getMemories());
  const [openZone, setOpenZone] = useState(null);
  const [selected, setSelected] = useState(null);
  const [collection, setCollection] = useState({ open: false, tab: "explore" });
  const [shopkeeper, setShopkeeper] = useState({ open: false, line: "Arre beta, kya chahiye?" });
  const [activity, setActivity] = useState(null);
  const [quest, setQuest] = useState({ active: false, need: [], got: [] });
  const [dark, setDark] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState("evening");
  const [raining, setRaining] = useState(false);
  const [powerCut, setPowerCut] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const cycleTod = () => setTimeOfDay((t) => TOD_ORDER[(TOD_ORDER.indexOf(t) + 1) % TOD_ORDER.length]);
  const toggleRain = () => setRaining((r) => { if (!r) sfx.thunder(); return !r; });

  const anyOverlay =
    !!openZone || !!selected || collection.open || !!activity || phoneOpen;

  const isTouch = useIsTouch();
  const { pos, setPos, facing, moving, moveTo } = useKeyboardMovement({
    initial: STREET_START,
    bounds: BOUNDS,
    enabled: !anyOverlay,
  });

  const tapToWalk = (e) => {
    if (anyOverlay) return;
    const r = e.currentTarget.getBoundingClientRect();
    moveTo(((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };

  useEffect(() => {
    fetchProducts()
      .then((d) => setProducts(d))
      .catch(() => toast.error("Store shelves failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const zones = scene === "store" ? STORE_ZONES : STREET_ZONES;

  const byZone = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      (m[p.zone] = m[p.zone] || []).push(p);
    });
    return m;
  }, [products]);

  const activeZone = useMemo(() => {
    let best = null;
    let bestD = THRESHOLD;
    for (const z of zones) {
      const d = Math.hypot(z.x - pos.x, z.y - pos.y);
      if (d < bestD) {
        bestD = d;
        best = z;
      }
    }
    return best;
  }, [zones, pos]);

  const interact = (zone) => {
    if (!zone) return;
    if (zone.type === "pco") {
      sfx.phone();
      setPhoneOpen(true);
      return;
    }
    if (zone.type === "door") {
      if (zone.id === "home-exit") {
        navigate("/");
        return;
      }
      if (zone.id === "shop-entrance") {
        setScene("store");
        setPos(STORE_START);
        toast("🔔 *ting* — Welcome to Sharma Kirana Store");
        setTimeout(() => setShopkeeper({ open: true, line: "Arre beta, kya chahiye?" }), 500);
      } else {
        setScene("street");
        setPos(STREET_START);
        toast("You step back out into the gali ☀️");
      }
      return;
    }
    if (zone.type === "npc") {
      setShopkeeper({ open: true, line: "Arre beta, kya chahiye?" });
      return;
    }
    if (zone.type === "fridge") {
      toast("🧊 khhhh… thandi hawa! *bottle clink*");
    }
    // shopping-list quest: fetching a listed shelf ticks it off
    if (quest.active && QUEST_POOL.includes(zone.id) && !quest.got.includes(zone.id)) {
      const got = [...quest.got, zone.id];
      setQuest((q) => ({ ...q, got }));
      if (got.length === quest.need.length) {
        toast.success("🛍️ Poori list mil gayi! Shabaash beta.");
        setTimeout(() => setQuest({ active: false, need: [], got: [] }), 2500);
      } else {
        toast(`✅ ${QUEST_LABELS[zone.id]} mil gaya! (${got.length}/${quest.need.length})`);
      }
    }
    setOpenZone(zone);
  };

  const openActivity = (id) => {
    if (id === "switch") {
      setDark((d) => { toast(d ? "💡 Light aa gayi!" : "🔦 Light chali gayi… mombatti jalao!"); return !d; });
      return;
    }
    if (id === "parchi") {
      const shuffled = [...QUEST_POOL].sort(() => Math.random() - 0.5).slice(0, 3);
      setQuest({ active: true, need: shuffled, got: [] });
      toast("📝 Mummy ne parchi di: ye saara saaman laa do!");
      return;
    }
    setActivity(id);
  };

  // rare power-cut event on the street — stays out until the player reconnects the wire
  useEffect(() => {
    if (scene !== "street" || powerCut) return undefined;
    const t = setTimeout(() => {
      setPowerCut(true);
      sfx.thunder();
      toast("💡 Arre light chali gayi!");
    }, 40000 + Math.random() * 55000);
    return () => clearTimeout(t);
  }, [scene, powerCut]);

  const fixWire = () => {
    setPowerCut(false);
    sfx.powerUp();
    toast("🔌 Taar jud gaya… light aa gayi! 💡");
  };

  // gali soundscape — starts on the street, follows time of day, stops elsewhere
  useEffect(() => {
    if (scene === "street") { startGali(timeOfDay); return () => stopGali(); }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);
  useEffect(() => { setGaliTime(timeOfDay); }, [timeOfDay]);

  // Enter to interact
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Enter" && !anyOverlay && activeZone) {
        e.preventDefault();
        interact(activeZone);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyOverlay, activeZone]);

  const handleAddMemory = (id) => {
    const updated = addMemory(id);
    setMemories(updated);
    toast.success("+1 Memory ❤️  Yaad unlock ho gayi!");
  };

  const openShelfFromShopkeeper = (zoneId) => {
    const z = STORE_ZONES.find((x) => x.id === zoneId);
    setShopkeeper({ open: false, line: "" });
    if (z) setOpenZone(z);
  };

  // touch d-pad
  const holdRef = useRef(null);
  const nudge = (dx, dy) => {
    setPos((p) => ({
      x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, p.x + dx)),
      y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, p.y + dy)),
    }));
  };
  const startHold = (dx, dy) => {
    if (anyOverlay) return;
    nudge(dx, dy);
    holdRef.current = setInterval(() => nudge(dx, dy), 40);
  };
  const stopHold = () => holdRef.current && clearInterval(holdRef.current);

  return (
    <div className="min-h-screen bg-background paper-grain">
      <AmbientMusic src="/audio/nostalgic.mp3" />
      {/* HUD */}
      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-kirana-red text-kirana-cream">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5">
          <div>
            <h1 className="font-signboard text-2xl font-bold leading-none sm:text-3xl">
              Kirana Store · किराना
            </h1>
            <p className="text-[11px] text-kirana-cream/80">Indian 90s & Early-2000s Nostalgia Museum</p>
          </div>
          <div className="flex items-center gap-2">
            <div
              data-testid="hud-memory-count"
              className="hidden items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-kirana-brown/30 px-3 py-1.5 text-sm font-bold sm:flex"
            >
              <Heart className="h-4 w-4 fill-kirana-cream" /> {memories.length} / 200+
            </div>
            <button
              data-testid="open-explore"
              onClick={() => setCollection({ open: true, tab: "explore" })}
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-gold px-3 py-1.5 text-sm font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm"
            >
              <Compass className="h-4 w-4" /> <span className="hidden sm:inline">Explore</span>
            </button>
            <button
              data-testid="open-memories"
              onClick={() => setCollection({ open: true, tab: "memories" })}
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5 text-sm font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm"
            >
              <Heart className="h-4 w-4" /> <span className="hidden sm:inline">Memories</span>
            </button>
            <Link
              data-testid="open-home"
              to="/"
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-kirana-brown/30"
            >
              <HomeIcon className="h-4 w-4" /> <span className="hidden sm:inline">Home</span>
            </Link>
            <Link
              data-testid="open-mohalla"
              to="/games"
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-kirana-brown/30"
            >
              <Gamepad2 className="h-4 w-4" /> <span className="hidden sm:inline">Mohalla Games</span>
            </Link>
            <Link
              data-testid="open-admin"
              to="/admin"
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-kirana-brown/30"
            >
              <Settings className="h-4 w-4" /> <span className="hidden sm:inline">Admin</span>
            </Link>
          </div>
        </div>
      </header>

      {/* scene */}
      <main className="mx-auto max-w-6xl px-3 py-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-signboard text-lg text-kirana-brown">
            {scene === "store" ? "🏪 Inside the shop" : "🛺 Out on the gali"}
          </span>
          <MoveHint verb="interact" />
        </div>

        <div
          data-testid="game-area"
          className="play-surface relative h-[68vh] min-h-[480px] w-full overflow-hidden rounded-xl border-[4px] border-kirana-brown hard-shadow"
        >
          {scene === "store" ? <StoreDecor /> : <StreetDecor timeOfDay={powerCut ? "night" : timeOfDay} />}
          {scene === "street" && <StreetNPCs />}

          {/* tap-to-move floor — below zones (z-20), above decor (z-10) */}
          <div data-testid="floor-tap" className="absolute inset-0 z-[15] cursor-pointer" onClick={tapToWalk} />

          {loading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-kirana-cream/70">
              <p className="font-signboard text-2xl text-kirana-brown">Dukaan khul rahi hai…</p>
            </div>
          )}

          {zones.map((z) => (
            <Zone
              key={z.id}
              zone={z}
              active={activeZone?.id === z.id}
              count={(byZone[z.id] || []).length}
              unlocked={(byZone[z.id] || []).filter((p) => memories.includes(p.id)).length}
              onClick={() => interact(z)}
            />
          ))}

          <Avatar pos={pos} facing={facing} moving={moving} />

          {/* Kirana interactions */}
          {scene === "store" && ACTIVITIES.map((a) => (
            <button
              key={a.id}
              data-testid={`activity-${a.id}`}
              onClick={() => openActivity(a.id)}
              style={{ left: `${a.x}%`, top: `${a.y}%` }}
              className="absolute z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5 rounded-md border-[3px] border-kirana-brown bg-kirana-gold/90 px-1.5 py-0.5 transition-transform hover:scale-110 hard-shadow-sm sm:px-2 sm:py-1"
            >
              <span className="text-base leading-none sm:text-xl">{a.id === "switch" ? (dark ? "🔦" : "💡") : a.emoji}</span>
              <span className="whitespace-nowrap font-signboard text-[9px] font-bold text-kirana-brown sm:text-[10px]">{a.label}</span>
            </button>
          ))}

          {/* mummy ki parchi checklist */}
          {scene === "store" && quest.active && (
            <div data-testid="quest-panel" className="absolute left-2 top-2 z-30 rounded-md border-2 border-kirana-brown bg-kirana-cream/95 p-2 text-xs shadow">
              <p className="mb-1 font-signboard text-sm font-bold text-kirana-brown">📝 Mummy ki Parchi</p>
              {quest.need.map((id) => (
                <div key={id} className={`flex items-center gap-1 font-semibold ${quest.got.includes(id) ? "text-kirana-green line-through" : "text-kirana-brown"}`}>
                  <span>{quest.got.includes(id) ? "☑" : "☐"}</span> {QUEST_LABELS[id]}
                </div>
              ))}
            </div>
          )}

          {/* load-shedding darkness (lantern follows the avatar) */}
          {scene === "store" && dark && (
            <div
              data-testid="dark-overlay"
              className="pointer-events-none absolute inset-0 z-[25]"
              style={{ background: `radial-gradient(circle 130px at ${pos.x}% ${pos.y}%, rgba(0,0,0,0) 0%, rgba(10,5,0,0.45) 45%, rgba(5,3,0,0.9) 78%)` }}
            />
          )}

          {/* street time-of-day + weather controls */}
          {scene === "street" && (
            <div className="absolute right-2 top-2 z-30 flex gap-1.5">
              <button data-testid="tod-cycle" onClick={cycleTod} className="rounded-md border-2 border-kirana-brown bg-kirana-cream/90 px-2 py-1 font-signboard text-xs font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm">
                {TOD_LABEL[timeOfDay]}
              </button>
              <button data-testid="rain-toggle" onClick={toggleRain} className="rounded-md border-2 border-kirana-brown bg-kirana-cream/90 px-2 py-1 font-signboard text-xs font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm">
                {raining ? "☔ Barish" : "☀️ Saaf"}
              </button>
            </div>
          )}

          {/* monsoon rain */}
          {scene === "street" && raining && (
            <>
              <div data-testid="rain-overlay" className="rain-layer pointer-events-none absolute inset-0 z-[24] opacity-70" />
              <div className="pointer-events-none absolute inset-0 z-[23] bg-[#2b3b4a]/25" />
            </>
          )}

          {/* power-cut darkness with a candle by the avatar */}
          {scene === "street" && powerCut && (
            <div
              data-testid="powercut-overlay"
              className="pointer-events-none absolute inset-0 z-[26]"
              style={{ background: `radial-gradient(circle 150px at ${pos.x}% ${pos.y}%, rgba(0,0,0,0.1) 0%, rgba(4,6,20,0.82) 68%)` }}
            >
              <div className="candle-flicker absolute" style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%,-140%)" }}>
                <span className="block h-3 w-1 rounded-full bg-[#ffcf6b] shadow-[0_0_16px_7px_rgba(255,190,90,0.75)]" />
              </div>
              {/* small notification + connect-wire option */}
              <div data-testid="powercut-notice" className="pointer-events-auto absolute left-1/2 top-4 -translate-x-1/2 rounded-lg border-[3px] border-kirana-brown bg-kirana-cream px-3 py-2 text-center hard-shadow">
                <p className="font-signboard text-sm font-bold text-kirana-brown">💡 Light chali gayi!</p>
                <p className="mb-1.5 text-[11px] text-kirana-brown/70">Taar dheela ho gaya — jodh do?</p>
                <button data-testid="fix-wire" onClick={fixWire} className="rounded-md border-2 border-kirana-brown bg-kirana-gold px-3 py-1 text-xs font-bold text-kirana-brown transition-transform hover:scale-105">
                  🔌 Taar Jodo
                </button>
              </div>
            </div>
          )}

        </div>

        {/* touch controls — below the scene so they never cover a card */}
        <div className={`touch-pad mx-auto mt-3 w-max grid-cols-3 gap-1.5 ${isTouch ? "grid" : "hidden"}`}>
          <span />
          <DPad onDown={() => startHold(0, -1.5)} onUp={stopHold} icon={ArrowUp} tid="dpad-up" />
          <span />
          <DPad onDown={() => startHold(-1.5, 0)} onUp={stopHold} icon={ArrowLeft} tid="dpad-left" />
          <DPad onDown={() => activeZone && interact(activeZone)} onUp={() => {}} label="⏎" tid="dpad-enter" />
          <DPad onDown={() => startHold(1.5, 0)} onUp={stopHold} icon={ArrowRight} tid="dpad-right" />
          <span />
          <DPad onDown={() => startHold(0, 1.5)} onUp={stopHold} icon={ArrowDown} tid="dpad-down" />
          <span />
        </div>
      </main>

      {/* overlays */}
      <ShopkeeperDialogue
        open={shopkeeper.open}
        line={shopkeeper.line}
        onClose={() => setShopkeeper({ open: false, line: "" })}
        onPick={openShelfFromShopkeeper}
      />

      <ZoneShelf
        open={!!openZone && !selected}
        onClose={() => setOpenZone(null)}
        zone={openZone}
        products={openZone ? byZone[openZone.id] || [] : []}
        memories={memories}
        onSelect={(p) => setSelected(p)}
      />

      <NostalgiaCard
        open={!!selected}
        product={selected}
        onClose={() => setSelected(null)}
        unlocked={selected ? memories.includes(selected.id) : false}
        onAdd={handleAddMemory}
      />

      <CollectionPanel
        key={collection.tab}
        open={collection.open}
        initialTab={collection.tab}
        onClose={() => setCollection((c) => ({ ...c, open: false }))}
        products={products}
        memories={memories}
        onSelect={(p) => setSelected(p)}
      />
      <WeighingGame open={activity === "weigh"} onClose={() => setActivity(null)} />
      <GolaMaker open={activity === "gola"} onClose={() => setActivity(null)} />
      <LuckyPack open={activity === "pack"} onClose={() => setActivity(null)} />
      <RotaryPhone open={phoneOpen} onClose={() => setPhoneOpen(false)} />
    </div>
  );
}

const DPad = ({ onDown, onUp, icon: Icon, label, tid }) => (
  <button
    data-testid={tid}
    onPointerDown={(e) => {
      e.preventDefault();
      onDown();
    }}
    onPointerUp={onUp}
    onPointerLeave={onUp}
    onContextMenu={(e) => e.preventDefault()}
    className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-kirana-brown bg-kirana-cream/90 text-kirana-brown hard-shadow-sm"
  >
    {Icon ? <Icon className="h-5 w-5" strokeWidth={3} /> : <span className="font-bold">{label}</span>}
  </button>
);
